/**
 * Thqah WhatsApp helpers — single source of truth for support number and
 * message-building so we don't sprinkle wa.me links across the codebase.
 *
 * Support number is configurable via REACT_APP_SUPPORT_WHATSAPP (E.164) and
 * falls back to the Thqah Qatar customer-service line.
 */
export const SUPPORT_WHATSAPP =
  (process.env.REACT_APP_SUPPORT_WHATSAPP || "+97455777200").replace(/[^0-9]/g, "");

function open(url) {
  // Open in a new tab. Using window.location on iOS WebView would replace
  // the current page; window.open + _blank stays compatible with both
  // browser and Capacitor wrappers.
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Open WhatsApp pre-filled with an influencer application message. */
export function applyAsInfluencer() {
  const lines = [
    "السلام عليكم، أرغب في الانضمام لبرنامج المؤثرين في ثقة.",
    "",
    "أرجو تزويدي بالتفاصيل والخطوات.",
  ];
  const text = encodeURIComponent(lines.join("\n"));
  open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${text}`);
}

/** Open WhatsApp pre-filled with the Thqah Official Evaluation request. */
export function startOfficialEvalRequest() {
  const lines = [
    "السلام عليكم،",
    "تم دفع رسوم تقييم ثقة الرسمي (500 ر.س).",
    "",
    "سأرسل الآن:",
    "• 6 صور حيّة واضحة للحلال من زوايا مختلفة",
    "• فيديو واضح للحلال (10–30 ثانية)",
    "",
    "أرجو إرسال التقرير: الشهادة + رأي الطبيب البيطري + السعر التقريبي.",
    "شكراً لكم.",
  ];
  const text = encodeURIComponent(lines.join("\n"));
  open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${text}`);
}

/** Open WhatsApp pre-filled with a Thqah Product order summary. */
export function confirmThqahProductOrder(order) {
  if (!order) return;
  const productAr = { camel: "للإبل 50 كيلو", horse: "للخيل 50 كيلو", falcon: "للطيور 10 كيلو" };
  const lines = [
    "السلام عليكم،",
    `تم دفع طلب منتج ثقة #${(order.id || "").slice(0, 8)}.`,
    "",
    `• المنتج: ${productAr[order.product] || order.product}`,
    `• العدد: ${order.qty} كيس`,
    `• الإجمالي: ${order.total_sar} ر.س`,
    "",
    "موقع التوصيل: (أرجو إرسال العنوان والإحداثيات هنا)",
    "",
    "شكراً لكم، بانتظار تأكيد الطلب وموعد التوصيل.",
  ];
  const text = encodeURIComponent(lines.join("\n"));
  open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${text}`);
}

/** Open WhatsApp with Thqah customer support, optionally pre-filling a topic. */
export function contactSupport({ topic, listingId } = {}) {
  const lines = [
    "السلام عليكم، أحتاج مساعدة من فريق ثقة.",
    topic ? `الموضوع: ${topic}` : null,
    listingId ? `رقم العرض: ${listingId}` : null,
  ].filter(Boolean);
  const text = encodeURIComponent(lines.join("\n"));
  open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${text}`);
}

/**
 * Open WhatsApp with the seller. The message includes the full listing
 * snapshot (title, price, type, color/breed, location, age, URL) so the
 * seller immediately knows which animal the buyer is referring to.
 */
export function contactSeller(item, opts = {}) {
  const phone = (item.whatsapp || item.owner_whatsapp || "").replace(/[^0-9]/g, "");
  if (!phone) return false;

  const CAT_AR = { camel: "إبل", horse: "خيل", falcon: "صقر" };
  const lines = [
    "السلام عليكم، أنا مهتم بهذا العرض من منصة ثقة:",
    "",
    `📌 ${item.title}`,
    `💰 السعر: ${Number(item.price_sar || 0).toLocaleString()} ر.س`,
    item.category ? `🐾 النوع: ${CAT_AR[item.category] || item.category}` : null,
    item.variant_label ? `🎨 الصنف/اللون: ${item.variant_label}` : null,
    item.location ? `📍 الموقع: ${item.location}` : null,
    item.age_years != null ? `🗓️ العمر: ${item.age_years} سنوات` : null,
    "",
    `🔗 https://thaqh.com/marketplace/listing/${item.id}`,
    opts.suffix || "",
  ].filter((l) => l !== null && l !== undefined);

  const text = encodeURIComponent(lines.join("\n"));
  open(`https://wa.me/${phone}?text=${text}`);
  return true;
}
